import "@/App.css";
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { Toaster } from "./components/ui/sonner";
import { Toaster as ToasterUI } from "./components/ui/toaster";
import { Loader2 } from "lucide-react";

// Offline-First (provider eager)
import { OfflineProvider } from "./context/OfflineContext";
import { OfflineBanner } from "./components/OfflineBanner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import PushNotificationPrompt from "./components/PushNotificationPrompt";
import FCMForegroundListener from "./components/FCMForegroundListener";
import offlineQuickQueue from "./services/offlineQuickQueue";
import { attachAutoSync } from "./services/resilientFetch";
import { installAuthInterceptor } from "./services/httpInterceptor";
import { BlockAgentFromCoopDashboard, CoopOnlyRoute, CoopOrAgentRoute } from "./components/RoleGuards";

// Install global axios 401 interceptor as early as possible
installAuthInterceptor();
attachAutoSync();

// Pages eager (entrée + auth + public QR — chargées immédiatement pour minimiser TTI)
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
const RegisterB2B = lazy(() => import("./pages/RegisterB2B"));
const PersonaLanding = lazy(() => import("./pages/PersonaLanding"));
const PourLesBailleursPage = lazy(() => import("./pages/PourLesBailleursPage"));
const PsecacaoPage = lazy(() => import("./pages/PsecacaoPage"));
const CommentCaMarchePage = lazy(() => import("./pages/CommentCaMarchePage"));
const PartnerDashboard = lazy(() => import("./pages/partner/PartnerDashboard"));
const B2BPartnersAdmin = lazy(() => import("./pages/admin/B2BPartnersAdmin"));
const CoopB2BRequests = lazy(() => import("./pages/cooperative/CoopB2BRequests"));
import ForgotPassword from "./pages/ForgotPassword";
import ActivateMember from "./pages/auth/ActivateMember";
import ActivateAgent from "./pages/auth/ActivateAgent";
import PublicTracePage from "./pages/public/PublicTracePage";

// Pages lazy (code-split par route — toutes les autres)
const Profile = lazy(() => import("./pages/Profile"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const BuyerOrders = lazy(() => import("./pages/buyer/BuyerOrders"));
const OrderTracking = lazy(() => import("./pages/OrderTracking"));
const ConditionsPage = lazy(() => import("./pages/ConditionsPage"));
const ConfidentialitePage = lazy(() => import("./pages/ConfidentialitePage"));
const SecuritePage = lazy(() => import("./pages/SecuritePage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccountPage"));

// Supplier Routes
const SupplierDashboard = lazy(() => import("./pages/supplier/Dashboard"));
const MyProducts = lazy(() => import("./pages/supplier/MyProducts"));
const Marketplace = lazy(() => import("./pages/supplier/Marketplace"));
const Orders = lazy(() => import("./pages/supplier/Orders"));
const Messages = lazy(() => import("./pages/supplier/Messages"));
const Notifications = lazy(() => import("./pages/supplier/Notifications"));
const SupplierAnalytics = lazy(() => import("./pages/supplier/Analytics"));
const DeliverySettings = lazy(() => import("./pages/supplier/DeliverySettings"));

// Farmer Routes
const FarmerDashboard = lazy(() => import("./pages/farmer/Dashboard"));
const USSDCarbonCalculator = lazy(() => import("./pages/farmer/USSDCarbonCalculator"));
// CarbonPaymentsDashboard supprimé — obsolète (PSE = versement direct au planteur par l'institution)
const MyHarvestsPage = lazy(() => import("./pages/farmer/MyHarvestsPage"));
const CarbonScorePage = lazy(() => import("./pages/farmer/CarbonScorePage"));
const RegisterFarmerPage = lazy(() => import("./pages/farmer/RegisterFarmerPage"));
const DeclarationRecoltePage = lazy(() => import("./pages/farmer/DeclarationRecoltePage"));
const FarmerReclamationsPage = lazy(() => import("./pages/farmer/FarmerReclamationsPage"));
const FarmerProtectionEnvPage = lazy(() => import("./pages/farmer/FarmerProtectionEnvPage"));

// Buyer Routes
const BuyerDashboard = lazy(() => import("./pages/buyer/Dashboard"));

// RSE Routes
const RSEDashboard = lazy(() => import("./pages/rse/Dashboard"));
const CarbonMarketplace = lazy(() => import("./pages/rse/CarbonMarketplace"));
const CreateCarbonListing = lazy(() => import("./pages/rse/CreateCarbonListing"));

// Admin Routes
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const SuperAdminDashboard = lazy(() => import("./pages/admin/SuperAdminDashboard"));
const CarbonBusinessDashboard = lazy(() => import("./pages/admin/CarbonBusinessDashboard"));
const BillingDashboard = lazy(() => import("./pages/admin/BillingDashboard"));
const PremiumAnalyticsDashboard = lazy(() => import("./pages/admin/PremiumAnalyticsDashboard"));
const ICIAnalyticsDashboard = lazy(() => import("./pages/admin/ICIAnalyticsDashboard"));
const ICIAlertsDashboard = lazy(() => import("./pages/admin/ICIAlertsDashboard"));
const CooperativeComparison = lazy(() => import("./pages/admin/CooperativeComparison"));
const RealTimeDashboard = lazy(() => import("./pages/admin/RealTimeDashboard"));
const SSRTEAnalytics = lazy(() => import("./pages/admin/SSRTEAnalytics"));
const CarbonAuditorsPage = lazy(() => import("./pages/admin/CarbonAuditorsPage"));
const AuditMissionsPage = lazy(() => import("./pages/admin/AuditMissionsPage"));
const BadgeAnalyticsPage = lazy(() => import("./pages/admin/BadgeAnalyticsPage"));
const AgentMapDashboard = lazy(() => import("./pages/admin/AgentMapDashboard"));
const AgentMapLeaflet = lazy(() => import("./pages/admin/AgentMapLeaflet"));
const UsersManagement = lazy(() => import("./pages/admin/UsersManagement"));
const ContentManagement = lazy(() => import("./pages/admin/ContentManagement"));
// AdminCarbonApprovals + CarbonPremiumsManagement supprimés — workflow obsolète (PSE = versement direct)
const QuotesManagement = lazy(() => import("./pages/admin/QuotesManagement"));
const AdminFarmerAssignment = lazy(() => import("./pages/admin/AdminFarmerAssignment"));
const AdminNotifications = lazy(() => import("./pages/admin/Notifications"));

// Auditor Routes
const AuditorDashboard = lazy(() => import("./pages/auditor/AuditorDashboard"));
const AuditorMissionPage = lazy(() => import("./pages/auditor/AuditorMissionPage"));
const AuditFormPage = lazy(() => import("./pages/auditor/AuditFormPage"));

// Cooperative Routes
const CooperativeDashboard = lazy(() => import("./pages/cooperative/Dashboard"));
const CooperativeLotsPage = lazy(() => import("./pages/cooperative/LotsPage"));
const CooperativeDistributionsPage = lazy(() => import("./pages/cooperative/DistributionsPage"));
const CooperativeReportsPage = lazy(() => import("./pages/cooperative/ReportsPage"));
const MemberParcelsPage = lazy(() => import("./pages/cooperative/MemberParcelsPage"));
const ImportMembersPage = lazy(() => import("./pages/cooperative/ImportMembersPage"));
const CooperativeNotifications = lazy(() => import("./pages/cooperative/Notifications"));
const FieldAgentsPage = lazy(() => import("./pages/cooperative/FieldAgentsPage"));
const AddParcelPage = lazy(() => import("./pages/cooperative/AddParcelPage"));
const CarbonPremiumsPage = lazy(() => import("./pages/cooperative/CarbonPremiumsPage"));
const SubscriptionPage = lazy(() => import("./pages/cooperative/SubscriptionPage"));
const CorrectiveActionsPage = lazy(() => import("./pages/cooperative/CorrectiveActionsPage"));
const PaymentSuccessPage = lazy(() => import("./pages/payment/PaymentResultPages").then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazy(() => import("./pages/payment/PaymentResultPages").then(m => ({ default: m.PaymentCancelPage })));
const CarbonSubmissionsPage = lazy(() => import("./pages/cooperative/CarbonSubmissionsPage"));
const ParcelsVerificationPage = lazy(() => import("./pages/cooperative/ParcelsVerificationPage"));
const DiscrepancyDashboard = lazy(() => import("./pages/cooperative/DiscrepancyDashboard"));
const AgentProgressPage = lazy(() => import("./pages/cooperative/AgentProgressPage"));
const USSDRegistrationsPage = lazy(() => import("./pages/cooperative/USSDRegistrationsPage"));
const CooperativeSSRTEDashboard = lazy(() => import("./pages/cooperative/SSRTEDashboard"));
const MRVDashboard = lazy(() => import("./pages/cooperative/MRVDashboard"));
const CoopHarvestsPage = lazy(() => import("./pages/cooperative/CoopHarvestsPage"));
const CooperativeReferralPage = lazy(() => import("./pages/cooperative/CooperativeReferralPage"));
const CarbonScoreAnalytics = lazy(() => import("./pages/cooperative/CarbonScoreAnalytics"));
const ARS1000Dashboard = lazy(() => import("./pages/cooperative/ARS1000Dashboard"));
const PDCListPage = lazy(() => import("./pages/cooperative/pdc/PDCListPage"));
const PDCStepperPage = lazy(() => import("./pages/cooperative/pdc/PDCStepperPage"));
const REDDGuidePage = lazy(() => import("./pages/REDDGuidePage"));
const REDDTrackingPage = lazy(() => import("./pages/cooperative/REDDTrackingPage"));

// Traceability Module
const TraceabilityDashboard = lazy(() => import("./pages/cooperative/traceability/TraceabilityDashboard"));
const CacaoFlowPage = lazy(() => import("./pages/cooperative/traceability/CacaoFlowPage"));
const SegregationPage = lazy(() => import("./pages/cooperative/traceability/SegregationPage"));
const TraceabilityReportsPage = lazy(() => import("./pages/cooperative/traceability/ReportsPage"));
const TraceabilityObjectivesPage = lazy(() => import("./pages/cooperative/traceability/ObjectivesPage"));

// Audit Module
const AuditDashboard = lazy(() => import("./pages/cooperative/audit/AuditDashboard"));
const AuditChecklistPage = lazy(() => import("./pages/cooperative/audit/ChecklistPage"));
const AuditNonConformitesPage = lazy(() => import("./pages/cooperative/audit/NonConformitesPage"));
const AuditReportsPage = lazy(() => import("./pages/cooperative/audit/AuditReportsPage"));
const RevueDirectionPage = lazy(() => import("./pages/cooperative/audit/RevueDirectionPage"));

// Formation Module
const FormationDashboard = lazy(() => import("./pages/cooperative/formation/FormationDashboard"));
const FormationProgrammePage = lazy(() => import("./pages/cooperative/formation/ProgrammePage"));
const FormationSessionsPage = lazy(() => import("./pages/cooperative/formation/SessionsPage"));
const FormationPVPage = lazy(() => import("./pages/cooperative/formation/PVPresencePage"));
const FormationAttestationsPage = lazy(() => import("./pages/cooperative/formation/AttestationsPage"));

// Gouvernance Module
const GouvernanceDashboard = lazy(() => import("./pages/cooperative/gouvernance/GouvernanceDashboard"));
const OrganigrammePage = lazy(() => import("./pages/cooperative/gouvernance/OrganigrammePage"));
const PolitiquePage = lazy(() => import("./pages/cooperative/gouvernance/PolitiquePage"));
const GouvernanceRevuePage = lazy(() => import("./pages/cooperative/gouvernance/RevueDirectionPage"));
const PlaintesPage = lazy(() => import("./pages/cooperative/PlaintesPage"));
const PSEContratsPage = lazy(() => import("./pages/cooperative/PSEContratsPage"));
const PSEScoringPage = lazy(() => import("./pages/cooperative/PSEScoringPage"));
const PSEAgroforestrySimulatorPage = lazy(() => import("./pages/cooperative/PSEAgroforestrySimulatorPage"));
const PSEPrimeHistoryPage = lazy(() => import("./pages/cooperative/PSEPrimeHistoryPage"));
const CoopPricingAdminPage = lazy(() => import("./pages/admin/CoopPricingAdminPage"));
const ShadeTreeAdminPage = lazy(() => import("./pages/admin/ShadeTreeAdminPage"));
const AuditorsManagementPage = lazy(() => import("./pages/admin/AuditorsManagementPage"));
const AuditorReadOnlyDashboard = lazy(() => import("./pages/auditor/AuditorReadOnlyDashboard"));
const ExportersManagementPage = lazy(() => import("./pages/admin/ExportersManagementPage"));
const ExporterDashboard = lazy(() => import("./pages/exporter/ExporterDashboard"));
const ExporterPremiumLayout = lazy(() => import("./pages/exporter/premium/ExporterPremiumLayout"));
const ExporterPortfolioView = lazy(() => import("./pages/exporter/premium/PortfolioView"));
const ExporterCooperativesView = lazy(() => import("./pages/exporter/premium/CooperativesView"));
const ExporterRiskMapView = lazy(() => import("./pages/exporter/premium/RiskMapView"));
const ExporterAuditReadyView = lazy(() => import("./pages/exporter/premium/AuditReadyView"));
const ExporterCustomerReportingView = lazy(() => import("./pages/exporter/premium/CustomerReportingView"));
const PartnersConsole = lazy(() => import("./pages/admin/PartnersConsole"));
const PublicCertificatVerifyPage = lazy(() => import("./pages/public/PublicCertificatVerifyPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const AgentMobilePage = lazy(() => import("./pages/agent/AgentMobilePage"));
const PSEEnterpriseDashboard = lazy(() => import("./pages/pse_enterprise/PSEEnterpriseDashboard"));

// Membres ARS 1000 Module
const MembresARSDashboard = lazy(() => import("./pages/cooperative/membres/MembresDashboard"));
const MembresStatsPage = lazy(() => import("./pages/cooperative/membres/MembresStatsPage"));
const CotisationsPage = lazy(() => import("./pages/cooperative/membres/CotisationsPage"));
const AdhesionPage = lazy(() => import("./pages/cooperative/membres/AdhesionPage"));
const RegistreMembresPage = lazy(() => import("./pages/cooperative/membres/RegistrePage"));
const PerimetreSMPage = lazy(() => import("./pages/cooperative/membres/PerimetrePage"));
const CarnetChampListPage = lazy(() => import("./pages/cooperative/carnet-champ/CarnetChampListPage"));
const CarnetChampDetailPage = lazy(() => import("./pages/cooperative/carnet-champ/CarnetChampDetailPage"));
const CarnetChampHistoryPage = lazy(() => import("./pages/cooperative/carnet-champ/CarnetChampHistoryPage"));
const SaisieRapidePage = lazy(() => import("./pages/agent/SaisieRapidePage"));

// ARS 1000 Consolide
const ARS1000ConsolidePage = lazy(() => import("./pages/cooperative/ARS1000ConsolidePage"));
const SimulationAuditPage = lazy(() => import("./pages/cooperative/SimulationAuditPage"));

// Risques & Durabilite Module
const RisquesDashboard = lazy(() => import("./pages/cooperative/risques/RisquesDashboard"));
const OutilsTerrainPage = lazy(() => import("./pages/cooperative/OutilsTerrainPage"));

// SSRTE Agent Routes
const SSRTEAgentDashboard = lazy(() => import("./pages/ssrte/SSRTEAgentDashboard"));
const SSRTERealTimeDashboard = lazy(() => import("./pages/ssrte/SSRTERealTimeDashboard"));

// Marketplace Hub (lié à Marketplace Intrants uniquement)
const MarketplaceHub = lazy(() => import("./pages/MarketplaceHub"));

// Buyer Dashboard
const BuyerMarketplaceDashboard = lazy(() => import("./pages/buyer/BuyerDashboard"));

// Messaging System
const MessagingPage = lazy(() => import("./pages/messaging/MessagingPage"));

// Agent Terrain
const AgentTerrainDashboard = lazy(() => import("./pages/agent/AgentTerrainDashboard"));
const JoinPdcPage = lazy(() => import("./pages/agent/JoinPdcPage"));
const PdcRolesPage = lazy(() => import("./pages/agent/PdcRolesPage"));

// Notifications (shared)
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const GuideAgroforesteriePage = lazy(() => import("./pages/shared/GuideAgroforesteriePage"));

// Active la file d'attente offline pour la saisie rapide (flush auto au retour du reseau)
offlineQuickQueue.install();

// Fallback Suspense — minimal, centré, marque visuelle Greenlink
const RouteFallback = () => (
  <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center" data-testid="route-loading-fallback">
    <Loader2 className="h-8 w-8 animate-spin text-[#1A3622]" />
  </div>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <OfflineProvider>
        <CartProvider>
          <BrowserRouter>
            <OfflineBanner />
            <FCMForegroundListener />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/trace/:lotCode" element={<PublicTracePage />} />
                <Route path="/verify-lot/:lotCode" element={<PublicTracePage />} />
                <Route path="/register" element={<Register />} />
                <Route path="/register-b2b" element={<RegisterB2B />} />
                {/* Phase 5.4 — Pages persona dédiées (Étape 1 : placeholder unifié) */}
                <Route path="/pour-les-cooperatives" element={<PersonaLanding />} />
                <Route path="/pour-les-planteurs" element={<PersonaLanding />} />
                <Route path="/pour-les-exportateurs" element={<PersonaLanding />} />
                <Route path="/pour-les-auditeurs" element={<PersonaLanding />} />
                <Route path="/pour-les-bailleurs" element={<PourLesBailleursPage />} />
                <Route path="/pse-cacao" element={<PsecacaoPage />} />
                <Route path="/comment-ca-marche" element={<CommentCaMarchePage />} />
                <Route path="/partner/dashboard" element={<PartnerDashboard />} />
                <Route path="/admin/b2b-partners" element={<B2BPartnersAdmin />} />
                <Route path="/cooperative/b2b-requests" element={<CoopB2BRequests />} />
                <Route path="/login" element={<Login />} />
                <Route path="/activate-member" element={<ActivateMember />} />
                <Route path="/activate-agent" element={<ActivateAgent />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/marketplace" element={<MarketplacePage />} />
                <Route path="/wishlist" element={<WishlistPage />} />
                <Route path="/order-tracking/:orderId" element={<OrderTracking />} />
                <Route path="/conditions" element={<ConditionsPage />} />
                <Route path="/confidentialite" element={<ConfidentialitePage />} />
                <Route path="/securite" element={<SecuritePage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/delete-account" element={<DeleteAccountPage />} />

                {/* Supplier/Fournisseur Routes */}
                <Route path="/supplier/dashboard" element={<SupplierDashboard />} />
                <Route path="/supplier/products" element={<MyProducts />} />
                <Route path="/supplier/marketplace" element={<Marketplace />} />
                <Route path="/supplier/orders" element={<Orders />} />
                <Route path="/supplier/messages" element={<Messages />} />
                <Route path="/supplier/notifications" element={<Notifications />} />
                <Route path="/supplier/analytics" element={<SupplierAnalytics />} />
                <Route path="/supplier/delivery-settings" element={<DeliverySettings />} />

                {/* Farmer/Producteur Routes */}
                <Route path="/farmer/dashboard" element={<FarmerDashboard />} />
                <Route path="/farmer/prime-pse" element={<USSDCarbonCalculator />} />
                {/* Rétrocompat : ancien lien Prime PSE redirige vers Prime PSE */}
                <Route path="/farmer/prime-carbone" element={<Navigate to="/farmer/prime-pse" replace />} />
                {/* /farmer/carbon-payments retiré — obsolète (PSE direct au planteur) */}
                <Route path="/farmer/my-harvests" element={<MyHarvestsPage />} />
                <Route path="/farmer/carbon-score" element={<CarbonScorePage />} />
                <Route path="/farmer/inscription" element={<RegisterFarmerPage />} />
                <Route path="/farmer/pdc" element={<PDCListPage />} />
                <Route path="/farmer/declaration-recolte" element={<DeclarationRecoltePage />} />
                <Route path="/farmer/reclamations" element={<FarmerReclamationsPage />} />
                <Route path="/farmer/protection-env" element={<FarmerProtectionEnvPage />} />
                <Route path="/guide-agroforesterie" element={<GuideAgroforesteriePage />} />
                {/* /carbon-payments retiré — obsolète (PSE direct au planteur) */}

                {/* Buyer/Acheteur Routes */}
                <Route path="/buyer/dashboard" element={<BuyerDashboard />} />
                <Route path="/buyer/orders" element={<BuyerOrders />} />

                {/* RSE/Enterprise Routes */}
                <Route path="/rse/dashboard" element={<RSEDashboard />} />
                <Route path="/rse/carbon-marketplace" element={<CarbonMarketplace />} />
                <Route path="/carbon-marketplace" element={<CarbonMarketplace />} />
                <Route path="/carbon-marketplace/create" element={<CreateCarbonListing />} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/analytics" element={<SuperAdminDashboard />} />
                <Route path="/admin/strategic" element={<SuperAdminDashboard />} />
                <Route path="/admin/carbon-business" element={<CarbonBusinessDashboard />} />
                <Route path="/admin/billing" element={<BillingDashboard />} />
                <Route path="/admin/premium-analytics" element={<PremiumAnalyticsDashboard />} />
                <Route path="/admin/ici-analytics" element={<ICIAnalyticsDashboard />} />
                <Route path="/admin/ici-alerts" element={<ICIAlertsDashboard />} />
                <Route path="/admin/cooperative-comparison" element={<CooperativeComparison />} />
                <Route path="/admin/realtime" element={<RealTimeDashboard />} />
                <Route path="/admin/ssrte-analytics" element={<SSRTEAnalytics />} />
                <Route path="/admin/notifications" element={<AdminNotifications />} />
                <Route path="/admin/carbon-auditors" element={<CarbonAuditorsPage />} />
                <Route path="/admin/audit-missions" element={<AuditMissionsPage />} />
                <Route path="/admin/badge-analytics" element={<BadgeAnalyticsPage />} />
                <Route path="/admin/agents-map" element={<AgentMapLeaflet />} />
                <Route path="/admin/geolocation" element={<AgentMapLeaflet />} />
                <Route path="/admin/agents-map-simple" element={<AgentMapDashboard />} />
                <Route path="/admin/users" element={<UsersManagement />} />
                <Route path="/admin/content" element={<ContentManagement />} />
                {/* /admin/carbon-approvals retiré — obsolète (PSE direct au planteur) */}
                <Route path="/admin/quotes" element={<QuotesManagement />} />
                <Route path="/admin/farmer-assignment" element={<AdminFarmerAssignment />} />
                {/* /admin/carbon-premiums retiré — obsolète (PSE direct au planteur) */}

                {/* Carbon Auditor Routes */}
                <Route path="/auditor/dashboard" element={<AuditorDashboard />} />
                <Route path="/auditor/mission/:missionId" element={<AuditorMissionPage />} />
                <Route path="/auditor/audit/:missionId/:parcelId" element={<AuditFormPage />} />
                <Route path="/auditor/missions" element={<AuditorDashboard />} />
                <Route path="/auditor/history" element={<AuditorDashboard />} />

                {/* Cooperative Routes */}
                <Route path="/cooperative/dashboard" element={<CoopOnlyRoute><CooperativeDashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/members" element={<Navigate to="/cooperative/membres/registre" replace />} />
                <Route path="/cooperative/members/new" element={<Navigate to="/cooperative/membres/adhesion" replace />} />
                <Route path="/cooperative/members/import" element={<CoopOnlyRoute><ImportMembersPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/members/:memberId/parcels" element={<CoopOrAgentRoute><MemberParcelsPage /></CoopOrAgentRoute>} />
                <Route path="/cooperative/parcels/new" element={<CoopOrAgentRoute><AddParcelPage /></CoopOrAgentRoute>} />
                <Route path="/cooperative/add-parcel" element={<CoopOrAgentRoute><AddParcelPage /></CoopOrAgentRoute>} />
                <Route path="/cooperative/parcels" element={<CoopOrAgentRoute><ParcelsVerificationPage /></CoopOrAgentRoute>} />
                <Route path="/cooperative/parcels/verification" element={<CoopOnlyRoute><ParcelsVerificationPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/ecarts" element={<CoopOnlyRoute><DiscrepancyDashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/lots" element={<CoopOnlyRoute><CooperativeLotsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/lots/new" element={<CoopOnlyRoute><CooperativeLotsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/distributions" element={<CoopOnlyRoute><CooperativeDistributionsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/reports" element={<CoopOnlyRoute><CooperativeReportsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/harvests" element={<CoopOnlyRoute><CoopHarvestsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/carbon-premiums" element={<CoopOnlyRoute><CarbonPremiumsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/subscription" element={<CoopOnlyRoute><SubscriptionPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/actions-correctives" element={<CoopOnlyRoute><CorrectiveActionsPage /></CoopOnlyRoute>} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                <Route path="/cooperative/carbon-submissions" element={<CoopOnlyRoute><CarbonSubmissionsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/carbon-submit" element={<CoopOnlyRoute><CreateCarbonListing /></CoopOnlyRoute>} />
                <Route path="/cooperative/notifications" element={<CoopOnlyRoute><CooperativeNotifications /></CoopOnlyRoute>} />
                <Route path="/cooperative/agents" element={<CoopOnlyRoute><FieldAgentsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/field-agents" element={<CoopOnlyRoute><FieldAgentsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/agents-progress" element={<CoopOnlyRoute><AgentProgressPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/inscriptions" element={<CoopOnlyRoute><USSDRegistrationsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/ssrte" element={<CoopOnlyRoute><CooperativeSSRTEDashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/mrv" element={<CoopOnlyRoute><MRVDashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/referral" element={<CoopOnlyRoute><CooperativeReferralPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/parrainage" element={<CoopOnlyRoute><CooperativeReferralPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/carbon-analytics" element={<CoopOnlyRoute><CarbonScoreAnalytics /></CoopOnlyRoute>} />
                <Route path="/cooperative/ars1000" element={<CoopOnlyRoute><ARS1000Dashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/pdc-v2" element={<CoopOrAgentRoute><PDCListPage /></CoopOrAgentRoute>} />
                <Route path="/cooperative/pdc-v2/:id" element={<CoopOrAgentRoute><PDCStepperPage /></CoopOrAgentRoute>} />
                <Route path="/cooperative/certification" element={<CoopOnlyRoute><ARS1000Dashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/traceability" element={<CoopOnlyRoute><TraceabilityDashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/traceability/flow" element={<CoopOnlyRoute><CacaoFlowPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/traceability/segregation" element={<CoopOnlyRoute><SegregationPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/traceability/reports" element={<CoopOnlyRoute><TraceabilityReportsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/traceability/objectives" element={<CoopOnlyRoute><TraceabilityObjectivesPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/audit" element={<CoopOnlyRoute><AuditDashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/audit/checklist" element={<CoopOnlyRoute><AuditChecklistPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/audit/non-conformites" element={<CoopOnlyRoute><AuditNonConformitesPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/audit/reports" element={<CoopOnlyRoute><AuditReportsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/audit/revue" element={<CoopOnlyRoute><RevueDirectionPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/formation" element={<CoopOnlyRoute><FormationDashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/formation/programme" element={<CoopOnlyRoute><FormationProgrammePage /></CoopOnlyRoute>} />
                <Route path="/cooperative/formation/sessions" element={<CoopOnlyRoute><FormationSessionsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/formation/pv" element={<CoopOnlyRoute><FormationPVPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/formation/attestations" element={<CoopOnlyRoute><FormationAttestationsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/gouvernance" element={<CoopOnlyRoute><GouvernanceDashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/gouvernance/organigramme" element={<CoopOnlyRoute><OrganigrammePage /></CoopOnlyRoute>} />
                <Route path="/cooperative/gouvernance/politique" element={<CoopOnlyRoute><PolitiquePage /></CoopOnlyRoute>} />
                <Route path="/cooperative/gouvernance/revue" element={<CoopOnlyRoute><GouvernanceRevuePage /></CoopOnlyRoute>} />
                <Route path="/cooperative/plaintes" element={<CoopOnlyRoute><PlaintesPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/pse-contrats" element={<CoopOnlyRoute><PSEContratsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/pse-scoring" element={<CoopOnlyRoute><PSEScoringPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/pse-simulateur" element={<CoopOnlyRoute><PSEAgroforestrySimulatorPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/pse-primes-history" element={<CoopOnlyRoute><PSEPrimeHistoryPage /></CoopOnlyRoute>} />
                <Route path="/admin/coop-pricing" element={<CoopPricingAdminPage />} />
                <Route path="/admin/shade-tree" element={<ShadeTreeAdminPage />} />
                <Route path="/admin/auditors" element={<AuditorsManagementPage />} />
                <Route path="/auditor/register" element={<Navigate to="/register-b2b" replace />} />
                <Route path="/auditor/read-only" element={<AuditorReadOnlyDashboard />} />
                <Route path="/exporter/register" element={<Navigate to="/register-b2b" replace />} />
                <Route path="/admin/exporters" element={<ExportersManagementPage />} />
                <Route path="/admin/partners" element={<PartnersConsole />} />
                <Route path="/exporter/dashboard" element={<ExporterDashboard />} />
                <Route path="/exporter/premium" element={<ExporterPremiumLayout />}>
                  <Route index element={<ExporterPortfolioView />} />
                  <Route path="portfolio" element={<ExporterPortfolioView />} />
                  <Route path="cooperatives" element={<ExporterCooperativesView />} />
                  <Route path="risk-map" element={<ExporterRiskMapView />} />
                  <Route path="audit-ready" element={<ExporterAuditReadyView />} />
                  <Route path="customer-reporting" element={<ExporterCustomerReportingView />} />
                </Route>
                <Route path="/public/pse/certificat/:certCode" element={<PublicCertificatVerifyPage />} />
                <Route path="/aide" element={<HelpPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/manuel" element={<HelpPage />} />
                <Route path="/agent/mobile" element={<AgentMobilePage />} />
                <Route path="/pse-enterprise/dashboard" element={<PSEEnterpriseDashboard />} />
                <Route path="/cooperative/membres" element={<CoopOnlyRoute><MembresARSDashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/membres/adhesion" element={<CoopOrAgentRoute><AdhesionPage /></CoopOrAgentRoute>} />
                <Route path="/cooperative/membres/registre" element={<CoopOrAgentRoute><RegistreMembresPage /></CoopOrAgentRoute>} />
                <Route path="/cooperative/membres/statistiques" element={<CoopOnlyRoute><MembresStatsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/membres/cotisations" element={<CoopOnlyRoute><CotisationsPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/membres/perimetre" element={<CoopOnlyRoute><PerimetreSMPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/carnet-champ" element={<CoopOrAgentRoute><CarnetChampListPage /></CoopOrAgentRoute>} />
                <Route path="/cooperative/carnet-champ/:carnetId" element={<CoopOrAgentRoute><CarnetChampDetailPage /></CoopOrAgentRoute>} />
                <Route path="/cooperative/carnet-champ/member/:memberId/historique" element={<CoopOrAgentRoute><CarnetChampHistoryPage /></CoopOrAgentRoute>} />
                <Route path="/agent/saisie-rapide" element={<SaisieRapidePage />} />
                <Route path="/cooperative/ars1000-readiness" element={<CoopOnlyRoute><ARS1000ConsolidePage /></CoopOnlyRoute>} />
                <Route path="/cooperative/simulation-audit" element={<CoopOnlyRoute><SimulationAuditPage /></CoopOnlyRoute>} />
                <Route path="/cooperative/risques" element={<CoopOnlyRoute><RisquesDashboard /></CoopOnlyRoute>} />
                <Route path="/cooperative/outils-terrain" element={<CoopOrAgentRoute><OutilsTerrainPage /></CoopOrAgentRoute>} />
                <Route path="/guide-redd" element={<REDDGuidePage />} />
                <Route path="/redd/tracking" element={<REDDTrackingPage />} />

                {/* SSRTE Agent Routes */}
                <Route path="/ssrte/dashboard" element={<SSRTEAgentDashboard />} />
                <Route path="/agent/ssrte" element={<SSRTEAgentDashboard />} />
                <Route path="/agent/terrain" element={<AgentTerrainDashboard />} />
                <Route path="/agent/search" element={<AgentTerrainDashboard />} />
                <Route path="/agent/join-pdc" element={<JoinPdcPage />} />
                <Route path="/agent/pdc-roles" element={<PdcRolesPage />} />
                <Route path="/agent/visite-pdc" element={<PDCListPage />} />
                <Route path="/ssrte/realtime" element={<SSRTERealTimeDashboard />} />
                <Route path="/ssrte/alerts" element={<SSRTERealTimeDashboard />} />

                {/* Shared Notifications */}
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/farmer/notifications" element={<NotificationsPage />} />
                <Route path="/rse/notifications" element={<NotificationsPage />} />
                <Route path="/buyer/notifications" element={<NotificationsPage />} />

                {/* Bourse des Récoltes : SUPPRESSION COMPLÈTE (juin 2026)
                    Composants frontend et endpoints backend supprimés physiquement.
                    Le positionnement public est désormais "Infrastructure PSE Agroforestier".
                    Routes supprimées : /marketplace/harvest, /harvest-marketplace,
                    /bourse-recoltes, /marketplace/create-listing, /marketplace/my-listings */}
                <Route path="/marketplaces" element={<MarketplaceHub />} />

                {/* Buyer Dashboard */}
                <Route path="/buyer/marketplace" element={<BuyerMarketplaceDashboard />} />
                <Route path="/buyer/quotes" element={<BuyerMarketplaceDashboard />} />
                <Route path="/buyer/favorites" element={<BuyerMarketplaceDashboard />} />
                <Route path="/buyer/alerts" element={<BuyerMarketplaceDashboard />} />

                {/* Messaging System */}
                <Route path="/messages" element={<MessagingPage />} />
                <Route path="/messages/:conversationId" element={<MessagingPage />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster />
          <ToasterUI />
          <PWAInstallPrompt />
          <PushNotificationPrompt />
        </CartProvider>
        </OfflineProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
